import clsx from 'clsx';
import React, { ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import SectionLoader, {
  LOADER_POSITION,
  LOADER_TYPE,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { RootState } from '@/src/reducers/types';
import { cn } from '@/src/util/ui';

import { useMoveFile } from './api/moveFile';
import FileViewer from './components/FileViewer/FileViewer';
import InfoPanel from './components/InfoPanel/InfoPanel';
import { TreatmentsContextProvider } from './components/InfoPanel/Treatment/store/treatments.store';
import SidebarActions, { SidebarSettings } from './components/SidebarActions/SidebarActions';
import { useFileEntryViewerSidebarActions } from './store/fileEntryViewer.sidebarActions.store';
import { useFileEntryViewer } from './store/fileEntryViewer.store';
import { useFileEntryViewerTreatment } from './store/fileEntryViewer.treatment.store';

import EntryCard from '../../../Elements/EntryCard/EntryCard';
import { useInbox } from '../../../store/inbox.store';
import { FileEntry } from '../../../types';
import { DeletedStaleEntrySubscriber } from '../useDeletedStaleEntrySubscriber';

const defaultSidebarSettings = {
  isDeleteEnabled: true,
  canMaximizeMinimize: true,
};

const defaultSettings = {
  isTransactionsEnabled: true,
  isViewInQuickbooksEnabled: true,
};

const fileViewerFulScreenClasses = {
  mainContainerClass: ['tw-absolute tw-inset-0 tw-z-[3]', 'tw-w-full', 'tw-bg-white'],
  entryCardClass: ['tw-h-full'],
};

export type FileEntryViewerSettings = {
  isTransactionsEnabled: boolean;
  isViewInQuickbooksEnabled?: boolean;
};

type FileEntryViewerProps = {
  fileEntry: FileEntry;
  mainContainerClass?: string;
  entryCardClass?: string;
  header: ReactElement;
  sidebarSettings?: SidebarSettings;
  settings?: FileEntryViewerSettings;
  isContainerFullScreen?: boolean;
  deletedStaleEntrySubscriber?: DeletedStaleEntrySubscriber;
};

const FileEntryViewer = ({
  fileEntry,
  mainContainerClass,
  entryCardClass,
  header,
  sidebarSettings = defaultSidebarSettings,
  settings = defaultSettings,
  deletedStaleEntrySubscriber = null,
}: FileEntryViewerProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isFullScreen, setFileEntry, activeTab, setActiveTab } = useFileEntryViewer();
  const { treatmentEvents } = useFileEntryViewerTreatment();
  const { sidebarActions } = useFileEntryViewerSidebarActions();
  const moveFileMutation = useMoveFile();
  const {
    markReadMutation,
    markMiscellaneousMutation,
    recordFileMutation,
    markReadMutationListener,
  } = sidebarActions;
  const isSidePanelCollapsed = useSelector((store: RootState) => store.common.isSidePanelCollapsed);
  const { isTransactionsEnabled } = useInbox();

  // On file opened event listener
  useEffect(() => {
    // BE will do this
    // Automatically open new files
    // if (fileEntry.status === InboxEntryStatus.New) {
    //   updateStatusMutationListener.mutateAsync({
    //     status: InboxEntryStatus.Open,
    //   });
    // }

    if (!fileEntry.isRead) {
      markReadMutationListener.mutateAsync({
        read: true,
      });
    }

    setFileEntry(fileEntry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetched setting still loading
  if (isTransactionsEnabled === null) {
    return <SectionLoader loaderType={LOADER_TYPE.screen} />;
  }

  const showLoading =
    markReadMutation.isLoading ||
    markMiscellaneousMutation.isLoading ||
    recordFileMutation.isLoading ||
    moveFileMutation.isLoading ||
    fileEntry.shouldWaitSocketUpdate;

  return (
    <ModalPortal isEnabled={isFullScreen} isHidden={markReadMutation.isLoading}>
      {showLoading && (
        <SectionLoader loaderType={LOADER_TYPE.screen} loaderPosition={LOADER_POSITION.relative} />
      )}
      {!showLoading && (
        <div
          className={cn(
            'card--selectedEntryPanel',
            'tw-grow tw-flex',
            'tw-p-[10px]',
            'tw-overflow-auto tw-scroll-styles',
            //prop class to set designated styles for main container ( e.g. width )
            isFullScreen
              ? [...fileViewerFulScreenClasses.mainContainerClass]
              : isSidePanelCollapsed
              ? 'tw-w-[calc(100vw-560px-110px)]'
              : 'tw-w-[calc(100vw-445px-345px)]',
            mainContainerClass,
            isFullScreen && 'tw-absolute'
          )}
          role="button"
          tabIndex={0}
          onKeyDown={deletedStaleEntrySubscriber?.onSyncDeletedStaleEntry}
          onClick={deletedStaleEntrySubscriber?.onSyncDeletedStaleEntry}
        >
          <EntryCard
            className={cn(
              'tw-flex tw-self-start tw-grow',
              'tw-rounded-[5px]',
              'hover:tw-bg-white',
              isFullScreen && [...fileViewerFulScreenClasses.entryCardClass],
              deletedStaleEntrySubscriber?.shouldWatchDeletedStaleEntryClicks &&
                'tw-pointer-events-none',
              //prop class to set designated styles for file card ( e.g. height )
              entryCardClass
            )}
            isActive={true}
          >
            <div
              className={clsx(
                'container--selectedEntryViewer',
                'tw-relative',
                'tw-w-[50%] tw-grow'
              )}
            >
              {/* header prop for different headers */}
              {header}

              <FileViewer
                file={{
                  id: fileEntry.file.id,
                  name: fileEntry.file.name,
                  rawName: fileEntry.file.rawName,
                  googleDriveFileUrl: fileEntry.file.googleDriveFileUrl,
                  isFileSupported: fileEntry.file.isFileSupported,
                }}
                key={fileEntry.file.rawName}
              />
            </div>
            <React.Fragment>
              <TreatmentsContextProvider
                fileId={fileEntry.file.id}
                isViewInQuickbooksEnabled={settings.isViewInQuickbooksEnabled}
                treatmentEvents={treatmentEvents}
              >
                <div
                  className={clsx(
                    'container--selectedEntryDetails',
                    'tw-border-l-[1px] tw-border-l-solid tw-border-l-cfo-grey-500',
                    'tw-w-[50%] tw-grow'
                  )}
                >
                  <InfoPanel
                    projectId={projectId}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    // transactionsEnabled={isTransactionsEnabled}
                    fileEntry={fileEntry}
                  />
                </div>

                <SidebarActions
                  fileEntry={fileEntry}
                  // isTransactionsEnabled={isTransactionsEnabled}
                  sidebarSettings={sidebarSettings}
                />
              </TreatmentsContextProvider>
            </React.Fragment>
          </EntryCard>
        </div>
      )}
      {deletedStaleEntrySubscriber && <deletedStaleEntrySubscriber.ConfirmRefreshDeletedModal />}
    </ModalPortal>
  );
};

export default FileEntryViewer;
